// `shortlist.session-info.service.ng1.ts` (`ShortlistSessionInfoService`) and the half of
// `shortlist.status.service.ng1.ts` (`ShortlistStatusService`) that is not UI: what host page the
// panel is open on, whether the panel is currently visible, and the `ShortlistOpened` /
// `ShortlistClosed` events.
//
// ⚠️ INSTALLED OUTSIDE REACT, like `installMouseflow`, because upstream's are app-wide singletons
// started at boot (`shortlistStatusServiceRun` injects both), and every analytics path — page
// views, list events, snippet events — reads them regardless of which layout is mounted.
//
// ⚠️ THE HOST IS ANALYTICS DATA, NOT DISPLAY. `host` / `subHost` ride in every shortlist event's
// extraInfo, and careero's `ActionTrackingHelperService.getHostSiteFromActionTracking` reads them
// back into the BI activity log's host site. Outside a frame nothing answers, and upstream records
// its own fallback, `None` — so does this.
import { IFrameClient, IFrameEventType, SHORTLIST_TARGET } from '@woo-ext/protocol'
import { shortlistSessionHostName, trackingPageUrl, trackShortlistEvent } from '@woo-ext/ui'
import { pageEntity } from './pageEntity.ts'

/** `IShortlistSessionHostInfo` — `atsHostType` is not analytics and is not kept here. */
export interface ShortlistSessionHostInfo {
  host: number
  subHost?: string | null
}

/** `{ host: ShortlistSessionHost.None }` — the `catch` arm of `init()`. */
const FALLBACK: ShortlistSessionHostInfo = { host: 0 }

let client: IFrameClient | undefined

/**
 * ⚠️ `target: 'shortlist'`, NOT THE FRAME'S OWN TARGET. Both upstream services pass the literal
 * (`target: 'shortlist'`) where every other message passes `window.targetFrame`, so even the
 * notifications frame asks the shortlist handler — `iframeClient()` would address it to nobody.
 */
function shortlistClient(): IFrameClient {
  client ??= new IFrameClient({ target: SHORTLIST_TARGET })
  return client
}

let sessionHostRequest: Promise<ShortlistSessionHostInfo> | undefined
let sessionHost: ShortlistSessionHostInfo | undefined

/**
 * `getSessionInfo()`'s host half — asked ONCE per document and cached, as `initPromise` is.
 * Three seconds without an answer, or no parent at all, is `None`.
 */
export function shortlistSessionHostInfo(): Promise<ShortlistSessionHostInfo> {
  sessionHostRequest ??= shortlistClient()
    .request<{ sessionHostInfo?: ShortlistSessionHostInfo } | undefined>(IFrameEventType.GetShortlistSessionHostInfo)
    .then(
      // Upstream assigns the payload as-is, and an answer without one crashes every later
      // `getSessionInfo()`; falling back instead keeps the events flowing with the honest value.
      (response) => response?.sessionHostInfo ?? FALLBACK,
      () => FALLBACK,
    )
    .then((info) => {
      sessionHost = info
      return info
    })
  return sessionHostRequest
}

/**
 * The answer, if it has arrived. Upstream's events `await getSessionInfo()`; the click handlers
 * here are synchronous, and the request goes out at boot and is answered in milliseconds, so by
 * the time a person clicks it is always here. Before it is, an event says `None` — which is what
 * upstream would have said had the shell not answered.
 */
export function shortlistSessionHostSnapshot(): ShortlistSessionHostInfo | undefined {
  return sessionHost
}

/** `isOpened` — undefined until asked, `null` when the parent did not answer. */
let isOpened: boolean | null | undefined

/** `isShortlistOpenedNoCache()`. A payload without `isOpened` stores `undefined`, as upstream's assignment does. */
async function isShortlistOpenedNoCache(): Promise<boolean | null | undefined> {
  try {
    const response = await shortlistClient().request<{ isOpened?: boolean } | undefined>(
      IFrameEventType.GetShortlistOpenedOrClosed,
    )
    isOpened = response?.isOpened
  } catch {
    isOpened = null
  }
  return isOpened
}

/** `isShortlistOpenedCached()` — asks again whenever the last answer was "no answer". */
export async function isShortlistOpenedCached(): Promise<boolean | null | undefined> {
  if (isOpened === undefined || isOpened === null) await isShortlistOpenedNoCache()
  return isOpened
}

/**
 * `data.getPageViewActionString` on the two product states: the page view's `action`.
 * ⚠️ `''` ONLY FOR "NO ANSWER" (`null`). An answer without the field is `undefined`, which is
 * falsy but not null, and upstream records it as `ShortlistHidden`.
 */
export async function pageViewAction(): Promise<string> {
  const opened = await isShortlistOpenedCached()
  if (opened === null) return ''
  return opened === true ? 'ShortlistVisible' : 'ShortlistHidden'
}

interface ShortlistOpenedOrClosedPayload {
  isOpened?: boolean
  handleClicked?: boolean
  newIndicationCounter?: number
}

interface OpenedEntityFields {
  id: number
  enrichedExtraExperience?: unknown
  enrichedRecordsAdded?: number
}

/**
 * `onShortlistOpenedOrClosed` (`shortlist.status.service.ng1.ts:73`).
 *
 * ⚠️ THREE THINGS HERE ARE QUIRKS, KEPT BECAUSE THEY ARE THE RECORDED FORMAT:
 *   - extraInfo is a hand-built JSON-ish string, not the `@@@` format — `"new":undefined` when
 *     the shell sends no counter, and `"enriched"` is `a && b`, so it can read `undefined`;
 *   - the page URL gains `host=<name>` in its query, rebuilt through `URL.searchParams` — the
 *     only event whose URL is not `$location.url()` verbatim;
 *   - `value` is `''`, not absent, when the page has no candidate, and the CONTACT pages never
 *     have one: upstream asks the candidate container only.
 */
async function trackOpenedOrClosed(data: ShortlistOpenedOrClosedPayload, host: ShortlistSessionHostInfo): Promise<void> {
  if (data.isOpened !== true) {
    trackShortlistEvent('ShortlistClosed', undefined, undefined, { fallbackValue: undefined })
    return
  }
  const url = trackingPageUrl()
  const candidate = (await pageEntity('candidate', window.location)) as OpenedEntityFields | undefined
  const handleClicked = data.handleClicked === true ? '"handleClicked":true,' : ''
  const counter = String(data.newIndicationCounter)
  const extraInfo =
    candidate === undefined
      ? `{${handleClicked}"new":${counter}}`
      : `{${handleClicked}"new":${counter},"enriched":${String(
          candidate.enrichedExtraExperience && (candidate.enrichedRecordsAdded ?? Number.NaN) > 0,
        )}}`
  // `new URL(basePath + $location.url(), frameHost)` … `.toString().replace(frameHost, '')`.
  const frameHost = `https://${window.location.hostname}`
  const withHost = new URL(url, frameHost)
  withHost.searchParams.set('host', String(shortlistSessionHostName(host.host)))
  trackShortlistEvent('ShortlistOpened', candidate === undefined ? '' : String(candidate.id), extraInfo, {
    url: withHost.toString().replace(frameHost, ''),
  })
}

/**
 * Start both services, as `shortlistStatusServiceRun` does at boot: ask for the host, then — in
 * every frame but the notifications one — listen for open/close and warm the opened cache.
 * Returns the listener's disposer.
 */
export function installShortlistSession(): () => void {
  let dispose: (() => void) | undefined
  let disposed = false
  const frameRole = new URLSearchParams(window.location.search).get('frameRole')
  // `init()` awaits the session info BEFORE subscribing, so an open/close that arrives first is
  // not recorded — upstream has no host to put in its URL yet.
  void shortlistSessionHostInfo().then((host) => {
    if (disposed || frameRole === 'notifications') return
    dispose = shortlistClient().onMessage((message) => {
      if (message.type !== IFrameEventType.ShortlistOpenedOrClosed) return
      const data = (message.data ?? {}) as ShortlistOpenedOrClosedPayload
      isOpened = data.isOpened
      trackOpenedOrClosed(data, host).catch(() => undefined)
    })
    // "warmup cache"
    void isShortlistOpenedNoCache()
  })
  return () => {
    disposed = true
    dispose?.()
  }
}
